'use client';
import React, { useState, useMemo } from 'react';
import CloudImage from '@/components/CloudImage';
import CustomGrid from '@/components/CustomGrid';
import ButtonContainer from '@/components/ButtonContainer';
import VehicleClassifierPopup from '@/components/vehicle-classifier/VehicleClassifierPopup';
import MediaContainer from '@/components/MediaContainer';
import MarkdownText from '@/components/MarkdownText';
import BookingButton from '@/components/BookingButton';
import Icon from '@/components/Icon';
import { FaCalendarCheck } from 'react-icons/fa';

export default function PackageSection({
  packageInfo = {
    title: '',
    subtitle: '',
    imagePosition: 'left', // "left" or "right"
    priceRange: '',
    includes: '',
    imageUrl: '',
    imageAlt: '',
    description: '',
    benefits: [],
    additionalInfo: [],
    brand: '', // e.g. "pro", "max-mf", "crystal"
    slug: '', // used for image path
    servicePricing: [], // array of { slug, price }
  },

  paintCorrectionLevel = {
    oneStep: {
      description:
        'One Step is a single-stage machine polishing process that removes light surface imperfections—think minor swirl marks, surface oxidation and water spots—to dramatically boost gloss and clarity.',
      idealFor:
        'Low-mileage or lightly worn vehicles with only superficial paint blemishes that need a quick visual pick-me-up.',
    },
    level1: {
      description:
        'Level 1 Paint Correction is a two-stage polishing process designed to tackle moderate paint defects. The first “cut” stage uses a more aggressive pad and polish combo to eliminate medium-level swirl marks, light scratches, and surface oxidation. In the second “refine” stage, we dial in the gloss and clarity to leave your finish smooth, deep, and like-new. This is our most popular correction package.',
      idealFor:
        'Daily-driven cars showing moderate swirl marks, light scratches or mild oxidation—drivers who want to bring their paint back to a near-factory shine without moving up to a multi-step master correction.',
    },
    level2: {
      description:
        'Level 2 Paint Correction is our ultimate, multi-stage polishing process designed to eradicate severe paint defects—think deep swirl marks, entrenched scratches, heavy oxidation, and etched water spots. Beginning with an aggressive “cut” stage to remove the toughest imperfections, we follow with one or more “refine” stages to polish the surface to its fullest depth and clarity. The end result is a showroom-quality finish that maximizes gloss and color pop.',
      idealFor:
        'Enthusiasts and show-car owners who demand perfection—vehicles with significant swirl, scratch damage, or neglected clear-coat that need the highest level of paint refinement.',
    },
  },
}) {
  const {
    title = '',
    subtitle = '',
    imagePosition = 'left',
    priceRange = '',
    includes = '',
    imageUrl = '',
    imageAlt = '',
    description = '',
    benefits = [],
    additionalInfo = [],
    brand = '',
    slug = '',
    servicePricing = [],
  } = packageInfo;

  // Determine if this brand should skip the paint-correction step
  const isMaxMF = brand.toLowerCase() === 'max-mf';

  // Dropdown state
  const [vehicleSize, setVehicleSize] = useState('');
  const [paintCorrection, setPaintCorrection] = useState('');

  // If max-mf, force correction to "none"
  const effectivePaintCorrection = isMaxMF ? 'none' : paintCorrection;
  const bothSelected = Boolean(
    vehicleSize && brand && (isMaxMF || paintCorrection),
  );

  // Layout classes
  const isRight = imagePosition === 'right';
  const imageOrderClasses = isRight
    ? 'order-first laptop:order-last'
    : 'order-first';
  const textOrderClasses = isRight
    ? 'order-last laptop:order-first'
    : 'order-last';

  // Build the slug and look up pricing
  const computedSlug = useMemo(() => {
    return bothSelected
      ? `${brand}-${vehicleSize}-${effectivePaintCorrection}`
      : '';
  }, [brand, vehicleSize, effectivePaintCorrection, bothSelected]);

  const matchedPricing = useMemo(() => {
    return bothSelected
      ? servicePricing.find((p) => p.slug === computedSlug)
      : null;
  }, [servicePricing, bothSelected, computedSlug]);

  const displayPrice = matchedPricing?.price ?? 'Not available';

  // Paint correction details lookup
  const paintCorrectionMapping = {
    'one-step': 'oneStep',
    'level-1': 'level1',
    'level-2': 'level2',
  };
  const selectedCorrection =
    !isMaxMF && paintCorrection && paintCorrectionMapping[paintCorrection]
      ? paintCorrectionLevel[paintCorrectionMapping[paintCorrection]]
      : null;

  return (
    <div className="w-full">
      <div className="mx-auto">
        <CustomGrid className="bg-secondary/10 h-full items-center gap-8 rounded-sm p-8 shadow-xl">
          {/* Image */}
          <div className={imageOrderClasses}>
            <MediaContainer className="tablet:aspect-4/3 laptop:aspect-3/4 aspect-square overflow-hidden rounded">
              <CloudImage
                publicId={`mad-web-app/serv-images/${slug}`}
                alt={imageAlt || title}
                fill
              />
            </MediaContainer>
          </div>

          {/* Text + Form */}
          <div className={`${textOrderClasses} space-y-4`}>
            <h2 className="tablet:text-3xl text-2xl font-bold text-gray-900">
              {title}
            </h2>
            <p className="text-lg font-semibold text-red-500">{subtitle}</p>
            <p className="text-lg font-semibold text-gray-800">{priceRange}</p>

            <MarkdownText>{description}</MarkdownText>
            <MarkdownText>{includes}</MarkdownText>

            <p className="mt-4 mb-2 font-semibold">Benefits</p>
            <MarkdownText>
              {benefits.map((b) => `- ${b}`).join('\n')}
            </MarkdownText>

            {additionalInfo.length > 0 && (
              <>
                <p className="mt-4 mb-2 font-semibold">Additional Benefits</p>
                <MarkdownText>
                  {additionalInfo.map((info) => `- ${info}`).join('\n')}
                </MarkdownText>
              </>
            )}

            {/* Selections */}
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="vehicleSize"
                  className="mb-1 block text-sm font-semibold"
                >
                  Vehicle Size
                </label>
                <select
                  id="vehicleSize"
                  className="bg-primary-foreground w-full rounded-sm border border-gray-300 px-3 py-2"
                  value={vehicleSize}
                  onChange={(e) => setVehicleSize(e.target.value)}
                >
                  <option value="">Select one…</option>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="x-large">X-Large</option>
                </select>
              </div>

              {!isMaxMF && (
                <div>
                  <label
                    htmlFor="paintCorrection"
                    className="mb-1 block text-sm font-semibold"
                  >
                    Paint Correction
                  </label>
                  <select
                    id="paintCorrection"
                    className="bg-primary-foreground w-full rounded-sm border border-gray-300 px-3 py-2"
                    value={paintCorrection}
                    onChange={(e) => setPaintCorrection(e.target.value)}
                  >
                    <option value="">Select one…</option>
                    <option value="one-step">One Step</option>
                    <option value="level-1">Level 1</option>
                    <option value="level-2">Level 2</option>
                  </select>
                </div>
              )}
            </div>

            {/* Correction Details */}
            {!isMaxMF && selectedCorrection && (
              <div className="mb-4 space-y-2 rounded-sm border border-gray-300 bg-gray-50 p-4">
                <p className="text-sm font-semibold">
                  Description:{' '}
                  <span className="font-normal">
                    {selectedCorrection.description}
                  </span>
                </p>
                <p className="text-sm font-semibold">
                  Ideal For:{' '}
                  <span className="font-normal">
                    {selectedCorrection.idealFor}
                  </span>
                </p>
              </div>
            )}

            {/* Price Display */}
            {bothSelected ? (
              <div className="text-2xl font-bold text-green-600">
                Price: ${displayPrice}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Please select required options to get the price.
              </p>
            )}

            {/* CTAs */}
            <ButtonContainer>
              <BookingButton>
                <Icon icon={FaCalendarCheck} className="mr-2" />
                Book Now
              </BookingButton>
              <VehicleClassifierPopup buttonVariant="tertiary" />
            </ButtonContainer>
          </div>
        </CustomGrid>
      </div>
    </div>
  );
}
